import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, TrendingUp, Wrench, ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function UnitCostSummaryPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedUnit, setExpandedUnit] = useState(null);
  const [unitDetail, setUnitDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const token = localStorage.getItem('token');
  const authHeaders = { 'Authorization': `Bearer ${token}` };

  const fetchSummary = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/work-orders/unit-cost-summary`, { headers: authHeaders });
      const d = await r.json();
      if (d.success) setData(d);
    } catch { toast.error('Failed to load cost summary'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const fetchUnitDetail = async (vehicleName) => {
    if (expandedUnit === vehicleName) { setExpandedUnit(null); return; }
    setExpandedUnit(vehicleName);
    setDetailLoading(true);
    try {
      const r = await fetch(`${API}/api/work-orders/unit-cost/${encodeURIComponent(vehicleName)}`, { headers: authHeaders });
      const d = await r.json();
      if (d.success) setUnitDetail(d);
    } catch { toast.error('Failed to load unit detail'); }
    setDetailLoading(false);
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground">Loading cost data...</div>;
  if (!data) return <div className="text-center py-8 text-muted-foreground">No data</div>;

  const { units, categories, fleet_totals } = data;
  const filtered = search ? units.filter(u => u.vehicle_name.toLowerCase().includes(search.toLowerCase())) : units;

  return (
    <div data-testid="unit-cost-summary" className="space-y-4">
      {/* Fleet Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-card border rounded-lg p-3">
          <div className="text-xs text-muted-foreground">Total Spend</div>
          <div className="text-xl font-bold text-emerald-400">${fleet_totals.total_cost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
        </div>
        <div className="bg-card border rounded-lg p-3">
          <div className="text-xs text-muted-foreground">Parts</div>
          <div className="text-xl font-bold text-blue-400">${fleet_totals.parts_cost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
        </div>
        <div className="bg-card border rounded-lg p-3">
          <div className="text-xs text-muted-foreground">Labor</div>
          <div className="text-xl font-bold text-purple-400">${fleet_totals.labor_cost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
        </div>
        <div className="bg-card border rounded-lg p-3">
          <div className="text-xs text-muted-foreground">Work Orders</div>
          <div className="text-xl font-bold">{fleet_totals.wo_count}</div>
        </div>
        <div className="bg-card border rounded-lg p-3">
          <div className="text-xs text-muted-foreground">Avg / WO</div>
          <div className="text-xl font-bold text-amber-400">${fleet_totals.avg_cost_per_wo.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
        </div>
        <div className="bg-card border rounded-lg p-3">
          <div className="text-xs text-muted-foreground">Units with Cost</div>
          <div className="text-xl font-bold">{fleet_totals.units_with_cost}</div>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="bg-card border rounded-xl p-4">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><Wrench className="h-4 w-4" />Spend by Category</h3>
        <div className="flex flex-wrap gap-2">
          {categories.map(c => {
            const pct = fleet_totals.parts_cost > 0 ? (c.total / fleet_totals.parts_cost * 100) : 0;
            return (
              <div key={c.category} className="bg-muted/20 rounded-lg px-3 py-2 text-xs">
                <div className="font-semibold">{c.category}</div>
                <div className="font-mono text-emerald-400">${c.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                <div className="text-muted-foreground">{c.count} items · {pct.toFixed(0)}%</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Search */}
      <Input placeholder="Search unit..." value={search} onChange={e => setSearch(e.target.value)} className="max-w-xs" />

      {/* Unit Table */}
      <div className="border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/30 border-b">
              <th className="text-left p-3 font-medium">Unit</th>
              <th className="text-right p-3 font-medium">WOs</th>
              <th className="text-right p-3 font-medium">Total</th>
              <th className="text-right p-3 font-medium hidden sm:table-cell">Parts</th>
              <th className="text-right p-3 font-medium hidden sm:table-cell">Labor</th>
              <th className="text-right p-3 font-medium">Avg/WO</th>
              <th className="text-right p-3 font-medium hidden md:table-cell">Predicted Next</th>
              <th className="p-3 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <React.Fragment key={u.vehicle_name}>
                <tr className="border-b hover:bg-muted/10 cursor-pointer transition-colors" onClick={() => fetchUnitDetail(u.vehicle_name)}>
                  <td className="p-3 font-mono font-bold">{u.vehicle_name}</td>
                  <td className="p-3 text-right">
                    <span className="text-xs">{u.wo_count}</span>
                    <span className="text-xs text-muted-foreground ml-1">({u.completed}C {u.open}O {u.pending}P)</span>
                  </td>
                  <td className="p-3 text-right font-mono font-bold text-emerald-400">${u.total_cost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                  <td className="p-3 text-right font-mono text-blue-400 hidden sm:table-cell">${u.parts_cost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                  <td className="p-3 text-right font-mono text-purple-400 hidden sm:table-cell">${u.labor_cost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                  <td className="p-3 text-right font-mono text-amber-400">${u.avg_cost_per_wo.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                  <td className="p-3 text-right font-mono text-amber-400/60 hidden md:table-cell">${u.avg_cost_per_wo.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                  <td className="p-3">{expandedUnit === u.vehicle_name ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</td>
                </tr>
                {expandedUnit === u.vehicle_name && (
                  <tr><td colSpan={8} className="p-0">
                    {detailLoading ? <div className="p-4 text-center text-xs text-muted-foreground">Loading...</div> : unitDetail ? (
                      <div className="p-4 bg-muted/5 space-y-3 border-t">
                        {/* Category breakdown for this unit */}
                        {unitDetail.categories.length > 0 && (
                          <div>
                            <h4 className="text-xs font-semibold text-muted-foreground mb-1">Repair Categories</h4>
                            <div className="flex flex-wrap gap-2">
                              {unitDetail.categories.map(c => (
                                <span key={c.category} className="text-xs px-2 py-1 rounded bg-muted/30">
                                  {c.category}: <span className="font-mono text-emerald-400">${c.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span> ({c.count})
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* WO history for this unit */}
                        <div>
                          <h4 className="text-xs font-semibold text-muted-foreground mb-1">Work Order History</h4>
                          <div className="space-y-1">
                            {unitDetail.work_orders.map(wo => (
                              <div key={wo.id} className="flex items-center gap-3 text-xs border-b border-border/30 py-1.5">
                                <span className="font-mono font-bold w-16">{wo.number}</span>
                                <span className={`px-1.5 py-0.5 rounded text-xs ${wo.status === 'Completed' ? 'bg-emerald-500/15 text-emerald-400' : wo.status === 'Open' ? 'bg-teal-500/15 text-teal-400' : 'bg-orange-500/15 text-orange-400'}`}>{wo.status}</span>
                                <span className="flex-1 text-muted-foreground truncate">{(wo.issues || []).map(i => i.name).join(', ') || wo.description || '—'}</span>
                                <span className="text-muted-foreground">{(wo.issued_at || '').substring(0, 10)}</span>
                                <span className="font-mono text-right w-20">{wo.total_amount > 0 ? `$${wo.total_amount.toFixed(2)}` : '—'}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Prediction */}
                        <div className="border-t pt-2 flex items-center gap-2 text-xs">
                          <TrendingUp className="h-4 w-4 text-amber-400" />
                          <span className="text-muted-foreground">Based on {unitDetail.summary.wo_count} work orders, predicted next service cost:</span>
                          <span className="font-mono font-bold text-amber-400">${unitDetail.summary.predicted_next_service.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                        </div>
                      </div>
                    ) : null}
                  </td></tr>
                )}
              </React.Fragment>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">No units found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
